import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import { FormField } from 'components/FormField/FormField';
import {
  TextAreaInput,
  type TextAreaInputProps,
} from 'components/TextAreaInput/TextAreaInput';
import type { FormikProps } from 'formik';
import type { ChangeEvent, JSX, ReactNode } from 'react';
import {
  type FormikValueAdapter,
  applyFormikValueAdapter,
} from 'utils/formik/FormikValueAdapter';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type Props<TFormikValues> = Omit<TextAreaInputProps, 'value' | 'onChange'> & {
  formik: FormikProps<TFormikValues>;
  label?: ReactNode;
  name: StringKey<TFormikValues> | NestedFormikName;
  onChange?: (value: string) => void;
  transformValue?: FormikValueAdapter<string>;
};

export const FormikTextAreaField = <TFormikValues,>({
  formik,
  label,
  onChange,
  transformValue,
  ...otherProps
}: Props<TFormikValues>): JSX.Element => {
  const field = formik.getFieldProps(otherProps.name);
  const { error } = formik.getFieldMeta(otherProps.name);
  const hasError = fieldHasError<TFormikValues>(formik, otherProps.name);
  const transformedValueController = applyFormikValueAdapter(
    field,
    transformValue,
  );
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    transformedValueController.onChange(e);
    onChange?.(e.target.value);
  };

  return (
    <FormField
      className={classNames(hasError && 'formik-error-scroll-anchor')}
      label={label}
      error={hasError ? error : undefined}
    >
      <TextAreaInput
        {...field}
        {...transformedValueController}
        {...otherProps}
        onChange={handleChange}
        hasError={hasError}
      />
    </FormField>
  );
};
